// components/SussieMessenger.tsx
import React from 'react';
import { Mission, PlayerProfile, IdolRank } from '../types';
import { X, Mail, Check, Briefcase } from 'lucide-react';
import { RANKS } from '../constants';

interface SussieMessengerProps {
  player: PlayerProfile;
  missions: Mission[];
  onClose: () => void;
  onAccept: (mission: Mission) => void;
}

export const SussieMessenger: React.FC<SussieMessengerProps> = ({ player, missions, onClose, onAccept }) => {
  const rankData = RANKS[player.rank] || RANKS[IdolRank.TRAINEE];
  const multiplier = rankData?.multiplier || 1;

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className="relative w-full max-w-2xl glass-panel p-8 rounded-lg border border-slate-700 shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors">
          <X size={24} />
        </button>

        <div className="flex items-center gap-4 mb-6 border-b border-slate-800 pb-4">
          <div className="p-3 bg-slate-800 rounded-full border border-slate-600">
            <Mail size={28} className="text-cyan-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-slate-100">Sussie's Messages</h2>
            <p className="text-slate-400">New opportunities have arrived from Jinu.</p>
          </div>
        </div>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-4 custom-scrollbar">
          {missions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>No new messages at this time.</p>
            </div>
          ) : (
            missions.map(mission => {
              const isAccepted = player.activeContracts.some(c => c.id === mission.id);
              const cashReward = (mission?.reward || 0) * multiplier;
              const fanReward = (mission?.fanReward || 0) * multiplier;

              return (
                <div key={mission.id} className={`p-4 rounded-lg border transition-all ${isAccepted ? 'bg-green-900/20 border-green-500/30' : 'bg-slate-900/50 border-slate-700'}`}>
                  <h3 className="font-bold text-lg text-cyan-300">{mission.title}</h3>
                  <p className="text-sm text-slate-400 mt-2 mb-4">{mission.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex gap-4">
                      <span className="font-mono text-green-400">+$ {cashReward.toLocaleString()}</span>
                      <span className="font-mono text-pink-400">+{fanReward.toLocaleString()} Fans</span>
                    </div>
                    <button
                      onClick={() => onAccept(mission)}
                      disabled={isAccepted}
                      className="px-4 py-2 text-xs font-bold rounded-md flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-slate-700 hover:bg-cyan-600 disabled:bg-slate-800 border border-slate-600"
                    >
                      {isAccepted ? <><Check size={14} /> Accepted</> : <><Briefcase size={14} /> Accept</>}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};